import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { getUserRole } from "@/services/auth/unifiedAuth";

type Props = {
  children: React.ReactNode;
};

export function AdminRoute({ children }: Props) {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function checkAdmin(targetUserId?: string, targetEmail?: string) {
      if (!targetUserId) {
        if (mounted) {
          setIsAdmin(false);
          setLoading(false);
        }
        return;
      }

      // logDebug level only
      if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
        console.log("[AdminRoute] Checando papel para:", targetEmail || targetUserId);
      }

      const role = await getUserRole(targetUserId);
      
      if (mounted) {
        if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
          console.log("[AdminRoute] Resultado do papel:", role);
        }
        setIsAdmin(role === "admin");
        setLoading(false);
      }
    }

    // Inicialização
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await checkAdmin(session.user.id, session.user.email);
      } else {
        const timer = setTimeout(() => {
          if (mounted && loading) {
            setLoading(false);
          }
        }, 3000);
        return () => clearTimeout(timer);
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
        console.log("[AdminRoute] Auth event:", event);
      }

      if (event === "SIGNED_OUT") {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      if (session?.user) {
        checkAdmin(session.user.id, session.user.email);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground font-medium">Verificando credenciais administrativas...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
      console.warn("[AdminRoute] Access denied. Redirecting to /admin/login");
    }
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}
